import React, { useCallback, useMemo, useRef, useState } from 'react';
import { PiHouseFill } from 'react-icons/pi';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { type HeroView, HomeShellBanner, PortalHome } from '@hopdrive/feature-portal-home';
import { OrderIndexPagination, OrderRendererRegistryProvider } from '@hopdrive/feature-order-index';
import type { OrderProductKey } from '@hopdrive/feature-order-index';

import DealerOrderIndexAdapter from '@features/orderIndex/DealerOrderIndexAdapter';
import { resolveOrderRenderers } from '@features/orderIndex/plugins';
import { openSalesContact } from '@hopdrive/feature-portal-products';
import { retryProducts, usePortalProductsState, useProductsFailed, useProductsLoaded, useRooftop, useUser } from '@store';

import HomeOrdersZone from './components/HomeOrdersZone';
import { HOME_PRODUCTS, useHomeOrderFeed } from './hooks/useHomeOrderFeed';
import { useHomeProductCounts } from './hooks/useHomeProductCounts';
import { useHomeScope } from './hooks/useHomeScope';
import { useRefetchOnVisible } from './hooks/useRefetchOnVisible';
import { type HomeWorkingRooftop, accessRefinement, baselineView, productRefinement, spansRooftops } from './homeModelLayers';
import { resolveHomeModel } from './resolveHomeModel';
import { isActiveOrder, isUpNextOrder, sliceHomeFeed } from './utils/homeOrderFeedSlice';
import { homeTabPath, resolveHomeTabKey } from './utils/homeRoutes';
import { foldGuestRideUpcoming, toHomeCounts } from './utils/mapProductCounts';

/** The page still renders the model it has. */
const COUNTS_ERROR_MESSAGE = 'Some of today’s orders or figures are unavailable right now.';

const PRODUCTS_ERROR_MESSAGE = 'We couldn’t load your products. Please try again.';

/** Shown when the mixed feed holds more of today's orders than it hydrated, so nothing drops silently. */
const TRUNCATION_MESSAGE = 'Today has more orders than shown here.';

const HOME_RENDERERS = resolveOrderRenderers(HOME_PRODUCTS);

const HOME_PAGE_SIZE = 10;
const noop = (): void => {};

/**
 * Home's composition root: reads the viewer, the working rooftop, and the portal products from the
 * store, the tab from the location, the hero view and the scope from page state; fetches today's
 * figures through each product's shared counts module; resolves the model; renders it through the
 * feature with the router's Link as every navigating element. Home is always scoped to the working
 * rooftop; a rooftop card switches the working rooftop as the app header's selector would, and the
 * whole portal follows. The band is fullscreen, as every product's is. It reads no setting.
 */
const HomeLayout: React.FC = () => {
  const user = useUser();
  const rooftop = useRooftop();
  const products = usePortalProductsState();
  const productsLoaded = useProductsLoaded();
  const productsFailed = useProductsFailed();
  const navigate = useNavigate();
  const location = useLocation();
  const scope = useHomeScope();
  const [heroView, setHeroView] = useState<HeroView>('combined');
  const [activePage, setActivePage] = useState(0);
  const [upNextPage, setUpNextPage] = useState(0);

  const role = user?.profile?.default_role;
  const activeTab = resolveHomeTabKey(location.pathname);

  // The same layers the resolver runs, so the data hooks fetch only what the page will show.
  const shown = useMemo(() => accessRefinement(products), [products]);
  const { orderTypes } = useMemo(() => productRefinement(shown), [shown]);
  // A viewer who spans rooftops gets the Orders by rooftop cards; Home itself is always scoped to the working rooftop.
  const spanning = spansRooftops(baselineView(role), scope.options);

  // The mixed order feed the Active Orders and Up next zones render, scoped to the working rooftop like the hero and cards.
  const ownedProductKeys = useMemo(() => orderTypes as OrderProductKey[], [orderTypes]);
  const homeFeed = useHomeOrderFeed({ rooftops: scope.rooftops, orderTypes: ownedProductKeys, workingRooftopId: scope.workingRooftopId });

  // The hero and product cards read the working rooftop alone; the Orders by rooftop cards read a separate
  // pass across every listed rooftop (its per-customer tallies), run only for a viewer who spans them.
  const productCounts = useHomeProductCounts({ customerIds: scope.customerIds, rooftops: scope.rooftops, orderTypes, breakdown: false });
  const listedRooftopIds = useMemo(() => scope.listed.map(facts => facts.id), [scope.listed]);
  const breakdownCounts = useHomeProductCounts({ customerIds: listedRooftopIds, rooftops: scope.listed, orderTypes, breakdown: true, skip: !spanning });

  // One stable caller reads the latest refetchers through a ref so the visibility listener binds once.
  const latestRefetch = useRef(() => {});
  latestRefetch.current = () => {
    productCounts.refetch();
    breakdownCounts.refetch();
    homeFeed.refetch();
  };
  const refetchers = useMemo(() => [() => latestRefetch.current()], []);
  useRefetchOnVisible(refetchers);
  const refetchAll = useCallback(() => latestRefetch.current(), []);

  const listedIds = useMemo(() => (spanning ? listedRooftopIds : []), [spanning, listedRooftopIds]);
  const counts = useMemo(
    () => toHomeCounts(foldGuestRideUpcoming(productCounts.byProduct), foldGuestRideUpcoming(breakdownCounts.byProduct), listedIds),
    [productCounts.byProduct, breakdownCounts.byProduct, listedIds],
  );

  const workingRooftop = useMemo<HomeWorkingRooftop | undefined>(
    () => (rooftop?.id !== undefined ? { id: Number(rooftop.id), name: rooftop.name ?? '', locationName: rooftop.location?.name ?? null, category: rooftop.category } : undefined),
    [rooftop?.id, rooftop?.name, rooftop?.location?.name, rooftop?.category],
  );

  // A rooftop card makes its rooftop the working rooftop, so the whole portal — Home included — follows.
  const { selectRooftop } = scope;

  const model = useMemo(
    () =>
      resolveHomeModel(
        {
          role,
          products,
          heroView,
          rooftopOptions: scope.options,
          workingRooftop,
          counts,
          activeTab,
        },
        { onSelectRooftop: selectRooftop, onContactSales: openSalesContact },
      ),
    [role, products, heroView, scope.options, workingRooftop, counts, activeTab, selectRooftop],
  );

  // Filled here so the resolver stays pure. An order zone is dropped while the feed loads or when
  // its slice is empty, so Home never shows an empty or loading order section.
  const modelWithOrders = useMemo(() => {
    const feedLoading = homeFeed.feed.loading;
    const activeSlice = sliceHomeFeed(homeFeed.feed, isActiveOrder, activePage, HOME_PAGE_SIZE);
    const upNextSlice = sliceHomeFeed(homeFeed.feed, isUpNextOrder, upNextPage, HOME_PAGE_SIZE);
    const zoneOf = (slice: typeof activeSlice, onPageChange: (page: number) => void) => ({
      headerAction: <OrderIndexPagination totalCount={slice.totalCount} page={slice.page} pageSize={HOME_PAGE_SIZE} pageSizeOptions={[HOME_PAGE_SIZE]} onPageChange={onPageChange} onPageSizeChange={noop} />,
      content: <HomeOrdersZone feed={slice} resolved={homeFeed.resolved} nowMs={homeFeed.nowMs} preferences={homeFeed.preferences} pageSize={HOME_PAGE_SIZE} />,
    });
    return {
      ...model,
      content: model.content.map(tab => ({
        ...tab,
        zones: tab.zones.flatMap(zone => {
          if (zone.key === 'active_orders') return feedLoading || activeSlice.totalCount === 0 ? [] : [{ ...zone, ...zoneOf(activeSlice, setActivePage) }];
          if (zone.key === 'up_next') return feedLoading || upNextSlice.totalCount === 0 ? [] : [{ ...zone, ...zoneOf(upNextSlice, setUpNextPage) }];
          return [zone];
        }),
      })),
    };
  }, [model, homeFeed, activePage, upNextPage]);

  // A failed products fetch outranks a figure failure: without products there is no model, so it
  // gets its own message and retries the products fetch rather than the figures.
  const countsFailed = Object.keys(productCounts.errors).length > 0 || Object.keys(breakdownCounts.errors).length > 0 || homeFeed.feed.error != null;
  const banner = productsFailed ? (
    <HomeShellBanner severity='error' message={PRODUCTS_ERROR_MESSAGE} actions={[{ key: 'retry', label: 'Retry', onSelect: retryProducts }]} />
  ) : countsFailed ? (
    <HomeShellBanner severity='error' message={COUNTS_ERROR_MESSAGE} actions={[{ key: 'retry', label: 'Retry', onSelect: refetchAll }]} />
  ) : homeFeed.truncated ? (
    <HomeShellBanner severity='info' message={TRUNCATION_MESSAGE} />
  ) : undefined;

  // One adapter for every order card on Home: the platform, the viewer, and one interaction layer —
  // one photo viewer, one set of dialogs, one chat for the whole page.
  return (
    <DealerOrderIndexAdapter>
      {homeFeed.observers}
      <OrderRendererRegistryProvider renderers={HOME_RENDERERS}>
        <PortalHome
          model={modelWithOrders}
          onTabChange={key => navigate(homeTabPath(key))}
          onHeroViewChange={setHeroView}
          linkComponent={Link}
          headerIcon={<PiHouseFill />}
          banner={banner}
          variant='fullscreen'
          pending={!productsLoaded || scope.loading || productCounts.loading}
        />
      </OrderRendererRegistryProvider>
    </DealerOrderIndexAdapter>
  );
};

export default HomeLayout;
